import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';
import Paper from '@mui/material/Paper';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import ShareIcon from '@mui/icons-material/Share';
import ReplayIcon from '@mui/icons-material/Replay';
import SendIcon from '@mui/icons-material/Send';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import Collapse from '@mui/material/Collapse';
import Link from '@mui/material/Link';
import Chip from '@mui/material/Chip';
import ScheduleIcon from '@mui/icons-material/Schedule';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VotePanel from '../components/session/VotePanel';
import ParticipantList from '../components/session/ParticipantList';
import ItemList from '../components/session/ItemList';
import { useSession } from '../context/SessionContext';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import JiraImport from '../components/jira/JiraImport';
import HistoryView from '../components/history/HistoryView';
import UserProfile from '../components/UserProfile';
import VotingProgressIndicator from '../components/session/VotingProgressIndicator';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import MuiAlert from '@mui/material/Alert';
import { pushEstimateToJira as pushEstimateToJiraApi, api, getAISuggestion } from '../utils/api';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import BugReportIcon from '@mui/icons-material/BugReport';
import { getParticipantForSession } from '../utils/sessionStorage';
import AddItemDialog from '../components/session/AddItemDialog';
import SyncSubtasksButton from '../components/session/SyncSubtasksButton';
import SubtaskDialog from '../components/session/SubtaskDialog';
import PreviousEstimatesPanel from '../components/session/PreviousEstimatesPanel';
import AsyncVotingView from '../components/session/AsyncVotingView';
import ItemComments from '../components/session/ItemComments';

// Custom Alert for Snackbar, renamed to avoid conflict
const SnackbarAlert = React.forwardRef(function SnackbarAlert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// Custom tab panel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`planning-poker-tabpanel-${index}`}
      aria-labelledby={`planning-poker-tab-${index}`}
      {...other}
      style={{ padding: '16px 0' }}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

// Helper function for tab accessibility
function a11yProps(index) {
  return {
    id: `planning-poker-tab-${index}`,
    'aria-controls': `planning-poker-tabpanel-${index}`,
  };
}

// Loom embed component with lazy loading - shows thumbnail until user clicks play
const LoomEmbed = ({ videoId }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  if (isLoaded) {
    return (
      <iframe
        src={`https://www.loom.com/embed/${videoId}`}
        frameBorder="0"
        allowFullScreen
        style={{
          width: '100%',
          height: '400px',
          borderRadius: '8px'
        }}
        title={`Loom video ${videoId}`}
      />
    );
  }

  return (
    <Box
      onClick={() => setIsLoaded(true)}
      sx={{
        position: 'relative',
        width: '100%',
        height: '400px',
        borderRadius: '8px',
        overflow: 'hidden',
        cursor: 'pointer',
        bgcolor: '#000',
        '&:hover .play-button': {
          transform: 'translate(-50%, -50%) scale(1.1)',
          bgcolor: 'primary.main',
        }
      }}
    >
      {/* Loom thumbnail */}
      <img
        src={`https://cdn.loom.com/sessions/thumbnails/${videoId}-with-play.gif`}
        alt="Video thumbnail"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
        onError={(e) => {
          // Fallback to static thumbnail if gif fails
          e.target.src = `https://cdn.loom.com/sessions/thumbnails/${videoId}-00001.jpg`;
        }}
      />
      {/* Play button overlay */}
      <Box
        className="play-button"
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 80,
          height: 80,
          borderRadius: '50%',
          bgcolor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s ease',
        }}
      >
        <PlayArrowIcon sx={{ fontSize: 48, color: 'white' }} />
      </Box>
      {/* Click to play text */}
      <Typography
        variant="caption"
        sx={{
          position: 'absolute',
          bottom: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          color: 'white',
          bgcolor: 'rgba(0, 0, 0, 0.6)',
          px: 2,
          py: 0.5,
          borderRadius: 1,
        }}
      >
        Click to play video
      </Typography>
    </Box>
  );
};

const Session = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const { currentOrganization } = useWorkspace();
  const organizationId = currentOrganization?.id || null;
  const {
    socket,
    session,
    participant,
    participants,
    items,
    activeItem,
    votes,
    loading,
    error,
    currentVote,
    isConnected,
    votedCount,
    totalVoters,
    initializing,
    setSession: setSessionInContext,
    setParticipant: setParticipantInContext,
    joinSession,
    castVote,
    setFinalEstimate,
    setActiveItemId,
    clearSession,
    reconnectSocket,
    fetchItems,
    updateParticipantVotingStatus,
    revealVotes,
    setCurrentVote,
    prepareFacilitatorSession,
    addMultipleItems,
    updateSessionDescription
  } = useSession();
  
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [joinAttempted, setJoinAttempted] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [showConnectionBanner, setShowConnectionBanner] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [reconnectTimer, setReconnectTimer] = useState(0);
  const [connectionError, setConnectionError] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [isSettingFinalEstimate, setIsSettingFinalEstimate] = useState(false);
  const [isRevealingVotes, setIsRevealingVotes] = useState(false);
  const [manualEstimateValue, setManualEstimateValue] = useState('');
  const [lastEstimatedJiraItem, setLastEstimatedJiraItem] = useState(null); // New state for JIRA push
  const [jiraPushStatus, setJiraPushStatus] = useState({ open: false, message: '', severity: 'info' }); // Snackbar state
  const [isAddItemDialogOpen, setIsAddItemDialogOpen] = useState(false); // <-- State for AddItemDialog visibility
  const [subtaskDialogOpen, setSubtaskDialogOpen] = useState(false); // State for SubtaskDialog visibility

  // Description panel state
  const [descriptionExpanded, setDescriptionExpanded] = useState(true);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');

  // JIRA auto-open dialog state
  const [jiraImportOpen, setJiraImportOpen] = useState(false);
  const [jiraAutoOpenChecked, setJiraAutoOpenChecked] = useState(false);
  const [jiraIsConfigured, setJiraIsConfigured] = useState(false);
  const [userPreferences, setUserPreferences] = useState({ autoOpenJiraImport: true });

  // AI Suggestion state
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [showAiSuggestion, setShowAiSuggestion] = useState(false);

  const theme = useTheme();
  
  // Store function refs to prevent useEffect dependencies causing infinite loops
  const fetchItemsRef = useRef(fetchItems);
  const joinSessionRef = useRef(joinSession);
  const prepareFacilitatorSessionRef = useRef(prepareFacilitatorSession);
  const setSessionInContextRef = useRef(setSessionInContext);
  const setParticipantInContextRef = useRef(setParticipantInContext);
  
  // Update refs when functions change
  useEffect(() => {
    fetchItemsRef.current = fetchItems;
    joinSessionRef.current = joinSession;
    prepareFacilitatorSessionRef.current = prepareFacilitatorSession;
    setSessionInContextRef.current = setSessionInContext;
    setParticipantInContextRef.current = setParticipantInContext;
  }, [fetchItems, joinSession, prepareFacilitatorSession, setSessionInContext, setParticipantInContext]);
  
  // Log items from context
  // useEffect(() => {
  //   console.log('[LOGGING] Session.js: items from useSession() updated. Count:', items ? items.length : 'N/A', items);
  // }, [items]);
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Try to join session when component mounts
  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const participantIdFromUrl = query.get('pid');

    // console.log('[Session.js Mount Effect] participant from context:', participant);
    // console.log('[Session.js Mount Effect] participantIdFromUrl:', participantIdFromUrl);
    // console.log('[Session.js Mount Effect] sessionId from params:', sessionId);
    // console.log('[Session.js Mount Effect] joinAttempted state:', joinAttempted);
    // console.log('[Session.js Mount Effect] session from context:', session);
    // console.log('[Session.js Mount Effect] loading from context:', loading);
    // console.log('[Session.js Mount Effect] initializing from context (SessionContext initializing):', initializing);

    if (initializing) { // Wait for SessionContext to finish its own initialization
      // console.log('[Session.js Mount Effect] SessionContext is initializing, waiting...');
      return;
    }

    if (joinAttempted) {
      // console.log('[Session.js Mount Effect] Join already attempted or in progress, skipping further logic in this effect run.');
      return;
    }

    // Case 0: Attempt to rejoin using participant data from localStorage
    // Check if participant is NOT in context OR if participant in context is not for THIS session
    if (sessionId && (!participant || (session && session.sessionId !== sessionId))) { 
      const storedParticipantData = getParticipantForSession(sessionId);
      if (storedParticipantData && storedParticipantData.id) {
        console.log(`[Session.js Mount Effect] Found stored participant ${storedParticipantData.id} for session ${sessionId}. Priming context and attempting rejoin.`);
        
        // Prime the context. This should trigger SessionContext's useEffect for socket creation.
        // We only have sessionId for the session object at this point from storage.
        setSessionInContextRef.current({ sessionId: sessionId, name: 'Loading...' }); // Provide minimal session data
        setParticipantInContextRef.current(storedParticipantData);
        setJoinAttempted(true); // Mark attempt early

        // Give a brief moment for context to update and socket to potentially initialize
        setTimeout(() => {
          console.log(`[Session.js Mount Effect] Timeout: Calling joinSession for ${sessionId}, participant ${storedParticipantData.id}`);
          joinSessionRef.current(sessionId, storedParticipantData.id);
        }, 200); // Increased delay slightly
        return; 
      }
    }

    // Case 1: Facilitator or returning participant ALREADY in context for THIS session
    if (participant && session && session.sessionId === sessionId) {
      // ... (existing logic for rejoin if already in context, socket connection check etc.)
      // This block should ideally run if the above priming + joinSession call succeeds and populates context
      console.log(`[Session.js Mount Effect] Participant ${participant.id} already in context for this session ${sessionId}.`);
      setJoinAttempted(true); // Mark join as handled
      if (socket && socket.connected) {
        socket.emit('join-session', { sessionId, participantId: participant.id || participant._id });
      } else if (socket) {
        socket.connect(); // If socket exists but not connected
      }
      // ... (fetch items if needed, etc.)
      return; 
    }

    // Case 2: Joining via an invite link (pid in URL)
    if (sessionId && participantIdFromUrl) {
      console.log(`[Session.js Mount Effect] Joining session ${sessionId} via URL param participantId ${participantIdFromUrl}.`);
      joinSessionRef.current(sessionId, participantIdFromUrl);
      setJoinAttempted(true);
      return; 
    }

    // Case 3a: User is authenticated, try to load as facilitator.
    // Only attempt this if there's no participantIdFromUrl, as that indicates a specific participant join attempt.
    if (isAuthenticated && user && user.id && !joinAttempted && sessionId && !participantIdFromUrl) {
        console.log('[Session.js Mount Effect] User is authenticated AND no pid in URL, attempting to load as facilitator for session:', sessionId);
        setJoinAttempted(true); 
        prepareFacilitatorSessionRef.current(sessionId, user.id)
          .then(success => {
            if (!success) {
              console.log('[Session.js Mount Effect] Failed to load as facilitator, navigating to dashboard.');
              navigate('/dashboard');
            }
          })
          .catch(err => {
            console.error('[Session.js Mount Effect] Error attempting to load facilitator session:', err);
            navigate('/dashboard');
          });
        return;
    }
    
    // Case 3b: Not authenticated, no stored participant, no pid in URL. Redirect to join page.
    if (!isAuthenticated && !participantIdFromUrl && sessionId && !loading && !initializing) { 
      console.log(`[Session.js Mount Effect] No participant, not auth, no stored, no PID. Redirecting to /join/${sessionId}.`);
      navigate(`/join/${sessionId}`);
      setJoinAttempted(true); 
      return;
    }

  }, [sessionId, location.search, joinAttempted, participant, session, loading, initializing, navigate, isAuthenticated, user, socket]); // Added socket
  
  // Set document title when session is loaded
  useEffect(() => {
    if (session) {
      document.title = `Planning Poker - ${session.name}`;
    } else {
      document.title = 'Planning Poker';
    }
  }, [session]);
  
  // Auto-select the first item if no item is selected
  useEffect(() => {
    if (items && items.length > 0 && !selectedItemId && !activeItem) {
      const firstItem = items[0];
      const itemId = firstItem.id || firstItem._id;
      // console.log('Auto-selecting first item:', itemId);
      setSelectedItemId(itemId);
    }
  }, [items, selectedItemId, activeItem]);
  
  // Effect to update connection status
  useEffect(() => {
    if (!isConnected) {
      setConnectionError('Not connected to server');
      setReconnecting(true);
      
      // Start a countdown for reconnection
      let countdown = 30;
      setReconnectTimer(countdown);
      
      const timer = setInterval(() => {
        countdown -= 1;
        setReconnectTimer(countdown);
        
        if (countdown <= 0) {
          clearInterval(timer);
          setReconnecting(false);
        }
      }, 1000);
      
      return () => clearInterval(timer);
    } else {
      setConnectionError(null);
      setReconnecting(false);
      setShowConnectionBanner(false);
    }
  }, [isConnected]);
  
  // Effect to fetch participants periodically
  useEffect(() => {
    if (sessionId && isConnected) { 
      // DISABLED: Initial fetch of participants also causes too many requests
      // console.log('Initial fetch of participants');
      // if (socket) {
      //   socket.emit('get-participants', { sessionId });
      // }
      
      // DISABLED: Commenting out periodic fetching to reduce request spam
      // const fetchParticipantsInterval = setInterval(() => {
      //   console.log('Fetching latest participants data');
      //   if (socket) {
      //     socket.emit('get-participants', { sessionId });
      //   }
      // }, 10000); // Refresh less frequently (10 seconds instead of 3)
      
      // return () => clearInterval(fetchParticipantsInterval);
    }
  }, [sessionId, isConnected, socket]);

  // Add an additional refresh when connection status changes
  useEffect(() => {
    // Refresh data whenever connection state changes to connected and we have session/participant context
    if (isConnected && sessionId && participant && session && session.sessionId === sessionId) {
      // console.log('[Session.js Connection Effect] Connection established/restored for current session. Refreshing data.');
      
      // Throttle fetchItems to prevent excessive API calls
      const throttledFetchItems = setTimeout(() => {
        fetchItemsRef.current(); // Fetch items for the current session
      }, 1000);

      // DISABLED: Request updated participant list (server should emit 'participants-list')
      // if (socket) {
      //   console.log('[Session.js Connection Effect] Emitting get-participants to refresh participant list.');
      //   socket.emit('get-participants', { sessionId });
      // }

      // If items are still empty after a short delay (e.g., initial load for a new participant), try fetching again.
      // This handles cases where the initial `session-data` might not have populated items quickly enough.
      if (items.length === 0) {
        // console.log('[Session.js Connection Effect] Items are still empty shortly after connection, attempting fetchItems again.');
        const delayedFetchItems = setTimeout(() => {
          if (items.length === 0 && isConnected && participant && session && session.sessionId === sessionId) { // Re-check conditions
            // console.log('[Session.js Connection Effect - Delayed] Items still empty, calling fetchItems.');
            fetchItemsRef.current();
          }
        }, 3000); // Increased delay to 3 seconds
        
        return () => {
          clearTimeout(throttledFetchItems);
          clearTimeout(delayedFetchItems);
        };
      }
      
      return () => clearTimeout(throttledFetchItems);

    } else if (isConnected && sessionId && (!participant || session?.sessionId !== sessionId)) {
      // console.log('[Session.js Connection Effect] Connected, but participant/session context is not for this session page. This might indicate a need to join or redirect.');
      // Further logic might be needed here if this state is problematic, e.g., redirecting to join page.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, sessionId, participant, session]); // Removed fetchItems to prevent infinite loop
  
  // Effect to handle participant updates when someone joins (lightweight, event-driven only)
  useEffect(() => {
    if (socket && isConnected && sessionId) {
      // Listen for participant join events to refresh the list
      const handleParticipantJoined = () => {
        // Only refresh if we're a facilitator and connected
        const currentIsFacilitator = participant && participant.role === 'facilitator';
        if (currentIsFacilitator && socket.connected) {
          // console.log('[Session.js] New participant joined, refreshing participant list for facilitator');
          socket.emit('get-participants', { sessionId });
        }
      };

      const handleParticipantLeft = () => {
        // Only refresh if we're a facilitator and connected  
        const currentIsFacilitator = participant && participant.role === 'facilitator';
        if (currentIsFacilitator && socket.connected) {
          // console.log('[Session.js] Participant left, refreshing participant list for facilitator');
          socket.emit('get-participants', { sessionId });
        }
      };

      // Listen for these specific events
      socket.on('participant-joined', handleParticipantJoined);
      socket.on('participant-removed', handleParticipantLeft);

      return () => {
        socket.off('participant-joined', handleParticipantJoined);
        socket.off('participant-removed', handleParticipantLeft);
      };
    }
  }, [socket, isConnected, sessionId, participant]);

  // Reset AI suggestion when active item changes
  useEffect(() => {
    setShowAiSuggestion(false);
    setAiSuggestion(null);
  }, [activeItem?.id]);

  // Check JIRA configuration and user preferences for auto-open feature
  useEffect(() => {
    const checkJiraAndPreferences = async () => {
      if (!isAuthenticated || !participant || participant.role !== 'facilitator') {
        return;
      }

      try {
        // Check JIRA credentials
        const credentialsUrl = organizationId
          ? `/jira/credentials-source?organizationId=${organizationId}`
          : '/jira/credentials-source';
        const credResponse = await api.get(credentialsUrl).catch(() => ({ data: { isConfigured: false } }));
        setJiraIsConfigured(credResponse.data?.isConfigured || false);

        // Fetch user preferences
        const prefResponse = await api.get('/users/me/settings/preferences').catch(() => ({ data: { autoOpenJiraImport: true } }));
        setUserPreferences(prefResponse.data || { autoOpenJiraImport: true });
      } catch (err) {
        console.error('Error checking JIRA config or preferences:', err);
      }
    };

    checkJiraAndPreferences();
  }, [isAuthenticated, participant, organizationId]);

  // Auto-open JIRA import dialog for empty sessions with JIRA connected
  useEffect(() => {
    if (
      !jiraAutoOpenChecked &&
      participant &&
      participant.role === 'facilitator' &&
      jiraIsConfigured &&
      userPreferences.autoOpenJiraImport &&
      items.length === 0 &&
      session &&
      !loading
    ) {
      setJiraAutoOpenChecked(true);
      // Small delay to ensure UI is ready
      setTimeout(() => {
        setJiraImportOpen(true);
        setTabValue(1); // Switch to Import tab
      }, 500);
    }
  }, [jiraAutoOpenChecked, participant, jiraIsConfigured, userPreferences.autoOpenJiraImport, items.length, session, loading]);

  // Copy invitation link to clipboard
  const handleCopyInviteLink = () => {
    const baseUrl = window.location.origin;
    const inviteLink = `${baseUrl}/join/${session.sessionId}`;
    navigator.clipboard.writeText(inviteLink).then(() => {
      setSnackbarOpen(true);
    }).catch(err => {
      // Fallback for permission issues
      fallbackCopyTextToClipboard(inviteLink);
    });
  };
  
  // Fallback copy method using document.execCommand
  const fallbackCopyTextToClipboard = (text) => {
    try {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      
      // Make the textarea out of viewport
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      if (successful) {
        setSnackbarOpen(true);
      } else {
        // console.error('Fallback: Unable to copy to clipboard');
        alert(`Copy this invitation link: ${text}`);
      }
      
      document.body.removeChild(textArea);
    } catch (err) {
      // console.error('Fallback: Oops, unable to copy', err);
      alert(`Copy this invitation link: ${text}`);
    }
  };
  
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  
  // Function to retry connection
  const handleRetryConnection = () => {
    setReconnecting(true);
    setReconnectTimer(0); // Trigger immediate reconnect
    
    if (session && participant) {
      setJoinAttempted(false); // Allow another join attempt
      // console.log('Retrying connection...');
      setConnectionError('Attempting to reconnect to the server...');
      
      // Directly try to reconnect the socket
      if (socket) {
        socket.connect();
        
        // Also try the reconnect function from context
        reconnectSocket(5);
      }
      
      // After a brief delay, fetch data to refresh the UI
      setTimeout(() => {
        if (session && session.sessionId) {
          // Throttle this fetchItems call too
          setTimeout(() => {
            fetchItemsRef.current();
          }, 1000);
          // DISABLED: Causes too many requests
          // if (socket && socket.connected) {
          //   socket.emit('get-participants', { sessionId: session.sessionId });
          // }
        }
      }, 2000);
    }
  };
  
  // Show loading screen while joining session
  if (loading) {
    return (
      <Backdrop open={true} sx={{ color: '#fff', zIndex: 9999 }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress color="inherit" sx={{ mb: 2 }} />
          <Typography variant="h6" sx={{ color: 'white' }}>
            Joining session...
          </Typography>
        </Box>
      </Backdrop>
    );
  }
  
  // Handle item selection
  const handleSelectItem = (itemId) => {
    // console.log('Item selected:', itemId);
    setSelectedItemId(itemId);
  };
  
  // Function to start estimation for an item
  const handleStartEstimation = (itemId) => {
    // console.log('Starting estimation for item:', itemId);
    const targetId = itemId || selectedItemId;
    if (!targetId) {
      // console.error('No item selected to start estimation');
      return;
    }
    
    // Always update the selected item ID
    setSelectedItemId(targetId);
    
    // If not connected, try to reconnect first
    if (!isConnected) {
      // console.warn('Not connected to server when trying to start estimation, attempting to reconnect');
      
      // Try to reconnect
      reconnectSocket(3);
      
      // Show a message to the user
      const tempMessage = document.createElement('div');
      tempMessage.textContent = 'Reconnecting to server... Please try again in a moment.';
      tempMessage.style.padding = '10px';
      tempMessage.style.backgroundColor = '#ffeb3b';
      tempMessage.style.borderRadius = '4px';
      tempMessage.style.marginBottom = '10px';
      
      const container = document.querySelector('.MuiContainer-root');
      if (container) {
        container.prepend(tempMessage);
        setTimeout(() => tempMessage.remove(), 3000);
      }
      
      return;
    }
    
    // Call the context function to set the active item
    setActiveItemId(targetId);
  };
  
  // Function to submit final estimate
  const handleSubmitEstimate = (estimate) => {
    if (!activeItem) return;
    
    const itemId = activeItem.id || activeItem._id;
    const currentItemDetails = { // Capture details before activeItem is cleared
      id: itemId,
      externalId: activeItem.externalId,
      title: activeItem.title,
      estimate: String(estimate)
    };

    // console.log('Session.js: handleSubmitEstimate called with estimate:', estimate, 'for itemId:', itemId);
    setFinalEstimate(itemId, String(estimate)); // This will trigger socket events and clear activeItem eventually

    if (currentItemDetails.externalId) {
      setLastEstimatedJiraItem(currentItemDetails);
    }

    setIsSettingFinalEstimate(false);
    setManualEstimateValue('');
  };

  // AI Suggestion handlers
  const handleGetAISuggestion = async () => {
    if (!activeItem) return;

    setIsLoadingAI(true);
    setShowAiSuggestion(true);
    try {
      const suggestion = await getAISuggestion(activeItem.id || activeItem._id, {
        title: activeItem.title,
        description: activeItem.description || ''
      });
      setAiSuggestion(suggestion);
      // Auto-fill the manual estimate field with the suggestion
      if (suggestion.suggestedEstimate) {
        setManualEstimateValue(suggestion.suggestedEstimate);
      }
    } catch (error) {
      setJiraPushStatus({
        open: true,
        message: error.response?.data?.msg || 'Failed to get AI suggestion',
        severity: 'error'
      });
      setShowAiSuggestion(false);
    } finally {
      setIsLoadingAI(false);
    }
  };

  const handleDismissAISuggestion = () => {
    setShowAiSuggestion(false);
    setAiSuggestion(null);
  };

  // Description editing handlers
  const handleEditDescription = () => {
    setEditedDescription(session?.description || '');
    setIsEditingDescription(true);
  };

  const handleSaveDescription = async () => {
    const success = await updateSessionDescription(editedDescription);
    if (success) {
      setIsEditingDescription(false);
    }
  };

  const handleCancelEditDescription = () => {
    setIsEditingDescription(false);
    setEditedDescription('');
  };

  // Helper to render description with linkified URLs and Loom embeds
  const renderDescription = (text) => {
    if (!text) return null;

    // Split text by URLs to process
    const parts = [];
    let lastIndex = 0;
    let match;

    const allUrlPattern = /https?:\/\/[^\s<]+/g;
    while ((match = allUrlPattern.exec(text)) !== null) {
      // Add text before URL
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: text.slice(lastIndex, match.index)
        });
      }

      const url = match[0];
      const loomMatch = url.match(/loom\.com\/(?:share|embed)\/([a-zA-Z0-9]+)/);

      if (loomMatch) {
        parts.push({
          type: 'loom',
          videoId: loomMatch[1],
          url: url
        });
      } else {
        parts.push({
          type: 'link',
          url: url
        });
      }

      lastIndex = match.index + url.length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({
        type: 'text',
        content: text.slice(lastIndex)
      });
    }

    return (
      <Box>
        {parts.map((part, index) => {
          if (part.type === 'text') {
            return (
              <Typography key={index} component="span" variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {part.content}
              </Typography>
            );
          } else if (part.type === 'loom') {
            return (
              <Box key={index} sx={{ my: 2 }}>
                <LoomEmbed videoId={part.videoId} />
              </Box>
            );
          } else if (part.type === 'link') {
            return (
              <Link
                key={index}
                href={part.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ wordBreak: 'break-all' }}
              >
                {part.url}
              </Link>
            );
          }
          return null;
        })}
      </Box>
    );
  };

  const getConfidenceColor = (confidence) => {
    switch (confidence) {
      case 'high':
        return 'success';
      case 'medium':
        return 'warning';
      case 'low':
      default:
        return 'default';
    }
  };

  const handlePushToJira = async () => {
    if (!lastEstimatedJiraItem) return;

    const { externalId, estimate } = lastEstimatedJiraItem;

    try {
      // Fetch user's JIRA settings from API
      const settingsResponse = await api.get('/users/me/settings/jira');
      const {
        isJiraApiTokenSet,
        jiraUrl,
        jiraUsername,
        jiraStoryPointFieldId
      } = settingsResponse.data;

      if (!isJiraApiTokenSet || !jiraUrl) {
        setJiraPushStatus({ open: true, message: 'JIRA not configured. Please configure your JIRA settings in User Settings.', severity: 'error' });
        return;
      }

      if (!jiraStoryPointFieldId) {
        setJiraPushStatus({ open: true, message: 'Story Point Field ID not configured. Please set it in User Settings.', severity: 'error' });
        return;
      }

      setJiraPushStatus({ open: true, message: `Pushing estimate ${estimate} to JIRA issue ${externalId}...`, severity: 'info' });

      await pushEstimateToJiraApi(externalId, {
        jiraUrl: jiraUrl,
        username: jiraUsername,
        storyPointFieldId: jiraStoryPointFieldId,
        estimate: estimate,
        organizationId: organizationId,
      });

      setJiraPushStatus({ open: true, message: `Successfully pushed estimate ${estimate} to JIRA issue ${externalId}.`, severity: 'success' });
      setLastEstimatedJiraItem(null); // Clear after successful push

    } catch (error) {
      // console.error('Failed to push estimate to JIRA:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.msg || error.message || 'An unknown error occurred.';
      setJiraPushStatus({ open: true, message: `Failed to push to JIRA: ${errorMessage}`, severity: 'error' });
      // Optionally, don't clear lastEstimatedJiraItem on failure, so user can retry or dismiss
    }
  };
  
  // Function to vote on the active item
  const handleLeaveSession = () => {
    clearSession();
    navigate('/dashboard');
  };

  // Callback for when a new item is manually added
  const handleManualItemAdded = (newItem) => {
    if (newItem) {
      addMultipleItems([newItem]); // addMultipleItems expects an array
    }
    // setIsAddItemDialogOpen(false); // Removed: Dialog closing is handled by its own onClose prop
  };

  // Render error message if there's a problem
  if (error && !isConnected && !session) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/')}>
          Back to Home
        </Button>
      </Box>
    );
  }

  // Check if user is a facilitator
  const isFacilitator = participant && participant.role === 'facilitator';
  const selectedItemDetails = items.find(item => (item.id || item._id) === selectedItemId);

  // Detect async estimation mode
  const isAsyncMode = session?.estimationMode === 'async';
  
  // console.log('Session.js render - activeItem:', activeItem, 'selectedItemId:', selectedItemId, 'selectedItemDetails:', selectedItemDetails);
  // console.log('Session.js render - currentVote from context:', currentVote);
  // console.log('Session.js render - isFacilitator:', isFacilitator, 'CONTEXT votedCount:', votedCount, 'CONTEXT totalVoters:', totalVoters, 'participant.isVoting:', participant?.isVoting);

  // console.log('[LOGGING] Session.js: Rendering. Current items count:', items ? items.length : 'N/A');

  return (
    <Box sx={{ flexGrow: 1, pt: 1, px: 3, pb: 3 }}>
      {/* Session header */}
      <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h5" component="h1">
                {session && session.name ? session.name : 'Planning Poker Session'}
              </Typography>
              {isAsyncMode && (
                <Chip
                  icon={<ScheduleIcon />}
                  label="Async Mode"
                  color="secondary"
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
            <Box display="flex" alignItems="center">
              {/* {participant && (
                <Typography variant="body2" color="text.secondary">
                  You are joined as <strong>{participant.name}</strong> ({participant.role})
                </Typography>
              )} */}
              {isFacilitator && participant && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={participant.isVoting === undefined ? true : participant.isVoting}
                      onChange={(e) => {
                        // console.log(`Session.js: Switch toggled. New checked state: ${e.target.checked}. Current participant.isVoting: ${participant?.isVoting}`);
                        updateParticipantVotingStatus(e.target.checked);
                      }}
                      name="isVotingToggle"
                      color="primary"
                      size="small"
                    />
                  }
                  label="Participate in Voting"
                  sx={{ ml: 1, mr: 1, '& .MuiTypography-root': { fontSize: '0.875rem' } }}
                />
              )}
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<ShareIcon />}
                onClick={handleCopyInviteLink}
                sx={{ ml: 2 }}
              >
                Copy Invite Link
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              variant="outlined" 
              color="primary" 
              onClick={handleLeaveSession}
              sx={{ mr: 1 }}
            >
              Leave Session
            </Button>
            <UserProfile />
          </Grid>
        </Grid>
      </Paper>

      {/* Session Description Panel - collapsible */}
      {(session?.description || isFacilitator) && (
        <Paper elevation={2} sx={{ mb: 3, overflow: 'hidden' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1.5,
              cursor: 'pointer',
              bgcolor: 'action.hover',
              '&:hover': { bgcolor: 'action.selected' }
            }}
            onClick={() => !isEditingDescription && setDescriptionExpanded(!descriptionExpanded)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {descriptionExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              <Typography variant="subtitle1" fontWeight="medium">
                Session Description
              </Typography>
              {!session?.description && isFacilitator && !descriptionExpanded && (
                <Typography variant="caption" color="text.secondary">
                  (Click to add)
                </Typography>
              )}
            </Box>
            {isFacilitator && !isEditingDescription && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setDescriptionExpanded(true);
                  handleEditDescription();
                }}
                sx={{ mr: 1 }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          <Collapse in={descriptionExpanded} unmountOnExit>
            <Box sx={{ p: 2, pt: 0 }}>
              {isEditingDescription ? (
                <Box>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={editedDescription}
                    onChange={(e) => setEditedDescription(e.target.value)}
                    placeholder="Add context for your team, e.g., Loom video links, meeting notes..."
                    sx={{ mb: 2 }}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveDescription}
                    >
                      Save
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<CancelIcon />}
                      onClick={handleCancelEditDescription}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              ) : session?.description ? (
                renderDescription(session.description)
              ) : isFacilitator ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No description yet. Click the edit button to add context for your team.
                </Typography>
              ) : null}
            </Box>
          </Collapse>
        </Paper>
      )}

      {/* Connection error banner - now less intrusive */}
      {(!isConnected || connectionError) && showConnectionBanner && (
        <Paper 
          elevation={2}
          sx={{ 
            mb: 3, 
            p: 1.5, 
            bgcolor: 'warning.light', 
            color: 'warning.contrastText',
            borderRadius: 1,
            borderLeft: '5px solid #ff9800',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            maxHeight: '80px',
            overflow: 'hidden',
            transition: 'all 0.3s ease'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ReplayIcon sx={{ 
              animation: reconnecting ? 'spin 2s linear infinite' : 'none', 
              '@keyframes spin': { 
                '0%': { transform: 'rotate(0deg)' }, 
                '100%': { transform: 'rotate(360deg)' } 
              } 
            }} />
            <Box>
              <Typography variant="body2" fontWeight="bold">
                Connection Issue
              </Typography>
              <Typography variant="caption">
                {connectionError}
                {reconnecting && reconnectTimer > 0 && ` (Reconnecting in ${reconnectTimer}s...)`}
              </Typography>
            </Box>
          </Box>
          <Box>
            <Button 
              variant="text" 
              color="inherit" 
              size="small"
              onClick={() => setShowConnectionBanner(false)}
              sx={{ mr: 1 }}
            >
              Dismiss
            </Button>
            <Button 
              variant="outlined" 
              color="inherit" 
              size="small"
              onClick={handleRetryConnection}
              startIcon={<ReplayIcon />}
              disabled={reconnecting && reconnectTimer < 10} // Less restriction on manual retries
            >
              Retry
            </Button>
          </Box>
        </Paper>
      )}

      {/* Tabs navigation */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="planning poker tabs">
          <Tab label="Estimation" {...a11yProps(0)} />
          {isFacilitator && <Tab label="Import" {...a11yProps(1)} />}
          {/* <Tab label="History" {...a11yProps(isFacilitator ? 2 : 1)} /> */}
        </Tabs>
      </Box>

      {/* Estimation Tab */}
      <TabPanel value={tabValue} index={0}>
        {isAsyncMode ? (
          /* Async Mode: Show AsyncVotingView */
          <AsyncVotingView sessionId={sessionId} />
        ) : (
          /* Real-time Mode: Show existing UI */
          <Grid container spacing={3}>
            {/* Left column - Lists */}
            <Grid item xs={12} md={4}>
              {/* Voting Progress Indicator */}
              {activeItem && (
                <VotingProgressIndicator
                  votedCount={votedCount}
                  totalVoters={totalVoters}
                  participants={participants}
                  activeItem={activeItem}
                />
              )}

              <ParticipantList
                participants={participants}
                isFacilitator={isFacilitator}
                currentParticipantId={participant?.id || participant?._id}
                sessionId={sessionId}
              />
              <ItemList
                items={items}
                activeItemId={activeItem ? (activeItem.id || activeItem._id) : null}
                selectedItemId={selectedItemId}
                onSelectItem={handleSelectItem}
                onStartEstimation={handleStartEstimation}
                isFacilitator={isFacilitator}
              />
            </Grid>

            {/* Right column - Active estimation */}
            <Grid item xs={12} md={8}>
            {activeItem ? (
              <Card elevation={3}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {activeItem.jiraUrl ? (
                        <a
                          href={activeItem.jiraUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          {activeItem.title}
                          <OpenInNewIcon fontSize="small" sx={{ opacity: 0.7 }} />
                        </a>
                      ) : (
                        activeItem.title
                      )}
                      {activeItem.externalId && (
                        <Typography
                          component="span"
                          variant="subtitle2"
                          sx={{ ml: 1, color: 'text.secondary' }}
                        >
                          ({activeItem.externalId})
                        </Typography>
                      )}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                      {isFacilitator && activeItem && activeItem.status === 'active' && (
                        <>
                          <Button
                            variant="contained"
                            color="secondary"
                            size="large"
                            onClick={() => {
                              const itemId = activeItem.id || activeItem._id;
                              console.log('Reveal Votes clicked. Socket connected:', socket?.connected, 'itemId:', itemId);
                              if (!socket?.connected) {
                                setJiraPushStatus({ open: true, message: 'Not connected to server. Please refresh the page.', severity: 'error' });
                                return;
                              }
                              if (!itemId) {
                                setJiraPushStatus({ open: true, message: 'No item selected', severity: 'error' });
                                return;
                              }
                              setIsRevealingVotes(true);
                              const success = revealVotes(itemId);
                              if (!success) {
                                setJiraPushStatus({ open: true, message: 'Failed to reveal votes. Please try again.', severity: 'error' });
                                setIsRevealingVotes(false);
                                return;
                              }
                              // Reset after a short delay (the socket event will update the UI)
                              setTimeout(() => setIsRevealingVotes(false), 2000);
                            }}
                            disabled={!isFacilitator || !activeItem || activeItem.status !== 'active' || isRevealingVotes}
                            sx={{ mr: 1, fontWeight: 'bold' }}
                          >
                            {isRevealingVotes ? <CircularProgress size={20} color="inherit" /> : 'Reveal Votes'}
                          </Button>
                          <Button 
                            variant="outlined"
                            color="warning"
                            size="small"
                            onClick={() => setActiveItemId(null)} // Stop estimation
                          >
                            Stop Estimation
                          </Button>
                        </>
                      )}
                      {activeItem.status === 'revealed' && isFacilitator && (
                        <Box sx={{ mt: 2, p:1, border: '1px dashed grey', borderRadius: '4px' }}>
                          {!isSettingFinalEstimate ? (
                            <>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: showAiSuggestion ? 2 : 0 }}>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="primary"
                                  onClick={() => setActiveItemId(activeItem.id || activeItem._id)} // Resume Voting
                                >
                                  Resume Voting
                                </Button>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="success"
                                  onClick={() => {
                                    const numericalVotes = votes.filter(v => !isNaN(Number(v.value)));
                                    const calculatedAverage = numericalVotes.length > 0
                                      ? (numericalVotes.reduce((sum, v) => sum + Number(v.value), 0) / numericalVotes.length).toFixed(1)
                                      : ''; // Default to empty or some placeholder if no num votes
                                    setManualEstimateValue(calculatedAverage); // Pre-fill with average
                                    setIsSettingFinalEstimate(true); // Show input field
                                  }}
                                >
                                  Enter Final Estimate
                                </Button>
                                {!showAiSuggestion && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    onClick={handleGetAISuggestion}
                                    disabled={isLoadingAI}
                                    startIcon={isLoadingAI ? <CircularProgress size={14} /> : <SmartToyIcon />}
                                  >
                                    Get AI Suggestion
                                  </Button>
                                )}
                              </Box>

                              {/* AI Suggestion Panel */}
                              {showAiSuggestion && (
                                <Box
                                  sx={{
                                    p: 1.5,
                                    bgcolor: 'rgba(59, 130, 246, 0.1)',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'rgba(59, 130, 246, 0.3)'
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <SmartToyIcon color="primary" fontSize="small" />
                                    <Typography variant="subtitle2" color="primary.main">
                                      AI Suggestion
                                    </Typography>
                                  </Box>

                                  {isLoadingAI ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <CircularProgress size={18} />
                                      <Typography variant="body2" color="text.secondary">
                                        Analyzing similar items...
                                      </Typography>
                                    </Box>
                                  ) : aiSuggestion ? (
                                    <>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                        <Typography variant="body2">
                                          Suggested: <strong>{aiSuggestion.suggestedEstimate || 'N/A'}</strong>
                                        </Typography>
                                        <Chip
                                          label={`${aiSuggestion.confidence} confidence`}
                                          size="small"
                                          color={getConfidenceColor(aiSuggestion.confidence)}
                                        />
                                      </Box>

                                      {aiSuggestion.similarItems && aiSuggestion.similarItems.length > 0 && (
                                        <Box sx={{ mb: 1 }}>
                                          <Typography variant="caption" color="text.secondary">
                                            Based on: {aiSuggestion.similarItems.slice(0, 3).map((s, i) =>
                                              `${s.title.substring(0, 15)}${s.title.length > 15 ? '...' : ''} (${s.estimate})`
                                            ).join(', ')}
                                          </Typography>
                                        </Box>
                                      )}

                                      <Box sx={{ display: 'flex', gap: 1 }}>
                                        {aiSuggestion.suggestedEstimate && (
                                          <Button
                                            variant="contained"
                                            size="small"
                                            color="primary"
                                            onClick={() => {
                                              setManualEstimateValue(aiSuggestion.suggestedEstimate);
                                              setIsSettingFinalEstimate(true);
                                            }}
                                          >
                                            Use This Estimate
                                          </Button>
                                        )}
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          onClick={handleDismissAISuggestion}
                                        >
                                          Dismiss
                                        </Button>
                                      </Box>
                                    </>
                                  ) : null}
                                </Box>
                              )}
                            </>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt:1 }}>
                              <TextField
                                label="Final Estimate"
                                variant="outlined"
                                size="small"
                                value={manualEstimateValue}
                                onChange={(e) => setManualEstimateValue(e.target.value)}
                                sx={{ width: '120px' }}
                              />
                              <Button
                                variant="contained"
                                color="success"
                                size="small"
                                onClick={() => handleSubmitEstimate(manualEstimateValue)}
                                disabled={manualEstimateValue.trim() === ''}
                              >
                                Confirm & Submit
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                  setIsSettingFinalEstimate(false);
                                  setManualEstimateValue('');
                                }}
                              >
                                Cancel
                              </Button>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                  
                  {activeItem.description && (
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {activeItem.description}
                    </Typography>
                  )}
                  
                  <VotePanel
                    activeItem={activeItem}
                    currentVote={currentVote}
                    onVote={(value) => {
                      // console.log('**** Session.js: VotePanel.onVote (setCurrentVote in context) called with value:', value);
                      setCurrentVote(value); // This is from useSession(), should update context currentVote state
                    }}
                    onConfirmVote={castVote}
                    votes={votes}
                    participants={participants}
                    isFacilitator={isFacilitator}
                    isConnected={isConnected}
                    votedCount={votedCount}
                    totalVoters={totalVoters}
                  />

                  {/* Show Previous Estimates Panel when votes are revealed */}
                  {activeItem.status === 'revealed' && manualEstimateValue && (
                    <PreviousEstimatesPanel
                      estimate={manualEstimateValue}
                      sessionId={sessionId}
                    />
                  )}

                  {/* Comments Section for Active Item */}
                  <ItemComments
                    itemId={activeItem.id || activeItem._id}
                    jiraKey={activeItem.jiraKey || activeItem.externalId}
                    jiraUrl={activeItem.jiraUrl}
                    isFacilitator={isFacilitator}
                  />
                </CardContent>
              </Card>
            ) : selectedItemId && selectedItemDetails ? (
              <Paper elevation={3} sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h5" component="h2" gutterBottom>
                  {selectedItemDetails.jiraUrl ? (
                    <a
                      href={selectedItemDetails.jiraUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: 'inherit',
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      {selectedItemDetails.title}
                      <OpenInNewIcon fontSize="small" sx={{ opacity: 0.7 }} />
                    </a>
                  ) : (
                    selectedItemDetails.title
                  )}
                </Typography>
                {selectedItemDetails.description && (
                  <Typography variant="body1" paragraph sx={{ mb: 2, color: 'text.secondary' }}>
                    {selectedItemDetails.description}
                  </Typography>
                )}
                {isFacilitator ? (
                  <Box>
                    <Typography variant="subtitle1" paragraph sx={{ mb: 2 }}>
                      Ready to estimate this item?
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      onClick={() => handleStartEstimation(selectedItemId)}
                      endIcon={<SendIcon />}
                      disabled={!isConnected}
                    >
                      Start Estimation
                    </Button>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Waiting for the facilitator to start estimation for this item.
                  </Typography>
                )}

                {/* Comments Section for Selected Item */}
                <ItemComments
                  itemId={selectedItemId}
                  jiraKey={selectedItemDetails.jiraKey || selectedItemDetails.externalId}
                  jiraUrl={selectedItemDetails.jiraUrl}
                  isFacilitator={isFacilitator}
                />
              </Paper>
            ) : (
              <Paper elevation={3} sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom>
                  No item selected
                </Typography>
                <Typography variant="body1">
                  Select an item from the list to begin estimation.
                </Typography>
              </Paper>
            )}
            
            {/* Display Push to JIRA option */}
            {lastEstimatedJiraItem && isFacilitator && (
              <Paper elevation={3} sx={{ mt: 2, p: 2, backgroundColor: '#141414', border: '1px solid #2a2a2a' }}>
                <Typography variant="subtitle1" gutterBottom>
                  JIRA Sync for "{lastEstimatedJiraItem.title}" ({lastEstimatedJiraItem.externalId})
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Final Estimate: <strong>{lastEstimatedJiraItem.estimate}</strong>
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handlePushToJira}
                    size="small"
                  >
                    Push to JIRA
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => setSubtaskDialogOpen(true)}
                    size="small"
                  >
                    Add Subtasks
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setLastEstimatedJiraItem(null)}
                  >
                    Dismiss
                  </Button>
                </Box>
              </Paper>
            )}
            
              {/* Session History View - Moved here from a tab */}
              <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid ' + theme.palette.divider }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Session History
                </Typography>
                <HistoryView sessionId={sessionId} itemsVersion={items.filter(it => it.status === 'estimated').length} />
              </Box>
            </Grid>
          </Grid>
        )}
      </TabPanel>

      {/* Import Tab - Only visible to facilitators */}
      {isFacilitator && (
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<BugReportIcon />}
              onClick={() => setJiraImportOpen(true)}
            >
              Import from JIRA
            </Button>
            <JiraImport
              sessionId={sessionId}
              open={jiraImportOpen}
              onClose={() => setJiraImportOpen(false)}
              onItemsImported={(importedItems) => {
                addMultipleItems(importedItems);
                setJiraImportOpen(false);
                if (importedItems.length > 0) {
                  setTabValue(0); // Switch back to Estimation tab
                }
              }}
            />
            <Button
              variant="outlined"
              onClick={() => setIsAddItemDialogOpen(true)}
            >
              + Add Item Manually
            </Button>
            <SyncSubtasksButton sessionId={sessionId} />
          </Box>
        </TabPanel>
      )}

      {/* Notification for copied invite link */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <SnackbarAlert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          Invitation link copied to clipboard!
        </SnackbarAlert>
      </Snackbar>
      
      {/* JIRA Push Status Snackbar */}
      <Snackbar
        open={jiraPushStatus.open}
        autoHideDuration={6000}
        onClose={() => setJiraPushStatus({ ...jiraPushStatus, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <SnackbarAlert 
          severity={jiraPushStatus.severity} 
          variant="filled"
          onClose={() => setJiraPushStatus({ ...jiraPushStatus, open: false })}
          sx={{ width: '100%' }}
        >
          {jiraPushStatus.message}
        </SnackbarAlert>
      </Snackbar>

      {/* AddItemDialog rendered at a suitable place, e.g., near the end of the main return, similar to other dialogs/modals */}
      <AddItemDialog
        open={isAddItemDialogOpen}
        onClose={() => setIsAddItemDialogOpen(false)}
        sessionId={sessionId}
        onItemAdded={handleManualItemAdded}
      />

      {/* SubtaskDialog for adding subtasks to estimated items */}
      {lastEstimatedJiraItem && (
        <SubtaskDialog
          open={subtaskDialogOpen}
          onClose={() => setSubtaskDialogOpen(false)}
          item={items.find(i => (i.id || i._id) === lastEstimatedJiraItem.id) || {
            id: lastEstimatedJiraItem.id,
            title: lastEstimatedJiraItem.title,
            externalId: lastEstimatedJiraItem.externalId
          }}
          onSubtasksCreated={(newSubtasks) => {
            setJiraPushStatus({
              open: true,
              message: `Successfully added ${newSubtasks.length} subtask(s).`,
              severity: 'success'
            });
          }}
        />
      )}
    </Box>
  );
};

export default Session; 